<template>
  <div class="ds-margin-y-24 ds-md-margin-y-48">
    <!-- Payment type: switch between pricing and refund -->
    <DsDropdown
      v-if="hasRefundFee"
      :text="filterLabels.paymentMethodFee"
      :label="paymentTypeLabel"
      :options="[
        { text: filterLabels.paymentPricing, value: 'payment' },
        { text: filterLabels.paymentRefundPricing, value: 'refund' }
      ]"
      width="279px"
      class="pricing-dropdown ds-margin-bottom-12"
      @dropdown-item-clicked="handlePricingDropDownItemClick"
    />
    <!-- Payment type: pricing -->
    <h3 v-else class="ds-heading-xsmall ds-margin-bottom-12">
      {{ filterLabels.paymentPricing }}
    </h3>
    <!-- Countries List -->
    <div v-if="computedFee.type !== 'countryFees'" class="ds-text-small ds-margin-top-24 ds-color-grey-450">
      {{ computedCountries }}
    </div>
    <!-- Fee type -->
    <div v-if="computedFee.type !== 'countryFees'" class="single-country-fee ds-margin-top-12">
      <!-- Processing fee -->
      <div class="ds-heading ds-margin-right-8 processing-fee">
        {{ processingFee }}
      </div>
      <p class="ds-heading ds-padding-right-8">
        +
      </p>
      <!-- Fee type: Interchange -->
      <div v-if="computedFee.type === 'interchange'" class="ds-display-flex ds-align-items-center">
        <span class="ds-heading">{{ filterLabels.interchange }}</span>
        <DsTooltip
          class="ds-margin-left-12"
          :description="filterLabels.interchangeExplanation"
          :link="interchangeLink"
        />
      </div>
      <!-- Fee type: Fee -->
      <div v-if="computedFee.type === 'fee'">
        <span class="ds-heading">{{ computedFee.title }}</span>
        <DsTooltip v-if="computedFee.text" class="ds-margin-left-12" :description="computedFee.text" />
      </div>
    </div>
    <!-- Fee type: CountryFees -->
    <template v-else>
      <div v-for="(item, index) in computedCountryFees" :key="index" class="ds-grid country-fees-row">
        <div
          v-if="item.col1"
          :class="[
            `ds-grid__col--${item.col2 ? '12' : '6'}`
          ]"
          class="ds-grid__col  ds-text-align-left ds-font-weight-bold ds-margin-top-12"
        >
          {{ item.col1 }}
        </div>
        <div v-if="item.col2" class="ds-grid__col ds-grid__col--6  ds-text-align-left">
          {{ item.col2 }}
        </div>
        <div
          :class="{ 'ds-margin-top-12': !item.col2 }"
          class="ds-grid__col ds-grid__col--6 ds-text-align-left ds-font-weight-bold"
        >
          {{ item.col3 }}
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { DsTooltip, DsDropdown } from 'awds'
import { mapperModuleNavigationLink } from '../../../core/data-layer/modules/mappers'
import { getFormattedAmount } from '../../utils/pricing'

const { locale: localeRef } = useI18n()
const store = useRoutesStore()
const { getUrl } = useRoutes(store.routes, localeRef)
const { localeGlobalContentData, labels } = useGlobalData()

const showDash = item => (item.submethod || item.customSubmethod) && item.channel

const props = defineProps({
  pricingData: { type: Object, required: true },
  countryData: { type: Array, required: true },
  submethods: { type: Array, required: true }
})

const paymentType = ref('payment')

const filterLabels = computed(() => {
  return labels.value
})

const interchangeLink = computed(() => {
  const link = mapperModuleNavigationLink(filterLabels.value.interchangeLink, getUrl)
  return {
    title: filterLabels.value.learnMore,
    url: link.url || {},
    openInNewWindow: link.openInNewWindow,
    nuxt: link.nuxt
  }
})

const processingFee = computed(() => {
  const getCurrency = id => localeGlobalContentData.value.globalDataCurrency?.find(i => i.sys.id === id)?.isoCode
  const fee = {
    processingFeeAmount: filterLabels.value.processingFeeAmount,
    processingFeeCurrency: filterLabels.value.processingFeeCurrency
  }
  return getFormattedAmount(fee.processingFeeAmount, getCurrency(fee.processingFeeCurrency?.sys?.id))
})

const paymentTypeLabel = computed(() => {
  return paymentType.value === 'refund' ? filterLabels.value?.paymentRefundPricing : filterLabels.value?.paymentPricing
})

const hasRefundFee = computed(() => {
  return props.pricingData.refundFee !== '' || props.pricingData.countryFees.find(i => !!i?.refundFee)
})

const computedFee = computed(() => {
  const showRefundFee = paymentType.value === 'refund'

  switch (true) {
    case props.pricingData.isFeeTypeInterchange === true && !(showRefundFee && props.pricingData.refundFee):
      return {
        type: 'interchange'
      }
    case props.pricingData.countryFees && props.pricingData.countryFees.length > 0:
      return {
        type: 'countryFees'
      }
    default:
      return {
        type: 'fee',
        title: showRefundFee ? `${props.pricingData.refundFee} ${props.pricingData.refundFeeExplanationTitle}` : `${props.pricingData.fee} ${props.pricingData.feeExplanationTitle}`,
        text: showRefundFee ? props.pricingData.refundFeeExplanationText : props.pricingData.feeExplanationText
      }
  }
})

const computedCountryFees = computed(() => {
  let filtered = props.pricingData.countryFees
  const showRefundFee = paymentType.value === 'refund'

  const getFee = (item, showRefund) => {
    if (showRefund) {
      return item.refundFee
    }
    return item.interchange === 'yes' ? paymentTypeLabel.value.interchange : item.fee
  }

  if (showRefundFee) {
    filtered = filtered.filter(i => i?.refundFee !== '')
  }

  return filtered.map(item => ({
    col1: getSubmethodContent(item, props.submethods),
    col2: item.country ? getCountriesContent(item.country) : '',
    col3: `${processingFee.value} + ${getFee(item, showRefundFee)}`
  }))
})

const handlePricingDropDownItemClick = (option) => {
  paymentType.value = option.value
}

const getCountriesContent = (items) => {
  return items.map(i => getCountryName(i.value)).join(', ')
}

const getSubmethodContent = (item) => {
  const getSubmethodName = id => props.submethods.find(i => i.id === id)?.name || ''
  return `${item.customSubmethod || ''}${getSubmethodName(item.submethod)}${showDash(item) ? ' - ' : ''}${getChannelName(item.channel)}`
}

const getChannelName = (id) => {
  const globalDataChannels = localeGlobalContentData.value.globalDataChannel
  return globalDataChannels.find(channel => channel.sys.id === id)?.name || ''
}

const getCountryName = (id) => {
  const globalDataCountries = localeGlobalContentData.value.globalDataCountry
  return globalDataCountries.find(country => country?.sys?.id === id)?.name
}

const computedCountries = computed(() => {
  const countriesList = props.countryData?.map(item => getCountryName(item?.country)).join(', ')
  return props.pricingData.isCountryGlobal
    ? filterLabels.value?.paymentMethodGlobal
    : countriesList
})

</script>
<script>
export default {
  name: 'PaymentMethodPricing'
}
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.processing-fee {
  white-space: nowrap;
}

.processing-fee::first-letter {
  margin-right: 4px;
}

.single-country-fee {
  @include screen('medium') {
    align-items: center;
    display: flex;
  }
}

.country-fees-row {
  width: calc(100% + #{$spacing-24});
}

.pricing-dropdown :deep(.ds-dropdown__button) {
  @include heading-xsmall;
}
</style>
